import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'

// ────────────────────────────────────────────────────
// Generic CRUD hooks factory
// ────────────────────────────────────────────────────
function createEntityHooks(entityKey: string, endpoint: string) {
  return {
    useList(params?: Record<string, any>) {
      return useQuery({
        queryKey: [entityKey, params],
        queryFn: async () => {
          const { data } = await apiClient.get(endpoint, { params })
          return Array.isArray(data) ? data : data?.data ?? data
        },
      })
    },
    useGet(id: string | undefined) {
      return useQuery({
        queryKey: [entityKey, id],
        queryFn: async () => {
          const { data } = await apiClient.get(`${endpoint}/${id}`)
          return data?.data ?? data
        },
        enabled: !!id,
      })
    },
    useCreate() {
      const qc = useQueryClient()
      return useMutation({
        mutationFn: async (body: any) => {
          const { data } = await apiClient.post(endpoint, body)
          return data?.data ?? data
        },
        onSuccess: () => { qc.invalidateQueries({ queryKey: [entityKey] }) },
      })
    },
    useUpdate() {
      const qc = useQueryClient()
      return useMutation({
        mutationFn: async ({ id, ...body }: any) => {
          const { data } = await apiClient.put(`${endpoint}/${id}`, body)
          return data?.data ?? data
        },
        onSuccess: () => { qc.invalidateQueries({ queryKey: [entityKey] }) },
      })
    },
    useDelete() {
      const qc = useQueryClient()
      return useMutation({
        mutationFn: async (id: string) => {
          const { data } = await apiClient.delete(`${endpoint}/${id}`)
          return data
        },
        onSuccess: () => { qc.invalidateQueries({ queryKey: [entityKey] }) },
      })
    },
  }
}

// ────────────────────────────────────────────────────
// Generic function invoke hook
// ────────────────────────────────────────────────────
export function useInvokeFunction(functionName: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: Record<string, any>) => {
      const { data } = await apiClient.post(`/functions/${functionName}`, params)
      return data?.data ?? data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['functions', functionName] })
    },
  })
}

// ============================================
// DASHBOARD
// ============================================
export function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: async () => {
      const { data } = await apiClient.get('/dashboard/stats')
      return data.data
    },
  })
}

export function useUpcomingTasks() {
  return useQuery({
    queryKey: ['dashboard', 'upcoming-tasks'],
    queryFn: async () => {
      const { data } = await apiClient.get('/dashboard/upcoming-tasks')
      return data.data
    },
  })
}

export function useTodayAppointments() {
  return useQuery({
    queryKey: ['dashboard', 'today-appointments'],
    queryFn: async () => {
      const { data } = await apiClient.get('/dashboard/today-appointments')
      return data.data
    },
  })
}

export function useRecentActivity() {
  return useQuery({
    queryKey: ['dashboard', 'recent-activity'],
    queryFn: async () => {
      const { data } = await apiClient.get('/dashboard/recent-activity')
      return data.data
    },
  })
}

// ============================================
// CLIENTS
// ============================================
export function useClients(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['clients', params],
    queryFn: async () => {
      const { data } = await apiClient.get('/clients', { params })
      return data
    },
  })
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: ['clients', id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/clients/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

export function useClientStats() {
  return useQuery({
    queryKey: ['clients', 'stats'],
    queryFn: async () => {
      const { data } = await apiClient.get('/clients/stats')
      return data.data
    },
  })
}

export function useCreateClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (clientData: any) => {
      const { data } = await apiClient.post('/clients', clientData)
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['notes'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['all-tasks-for-cases'] })
    },
  })
}

export function useUpdateClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...clientData }: any) => {
      const { data } = await apiClient.put(`/clients/${id}`, clientData)
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
  })
}

export function useDeleteClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete(`/clients/${id}`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
  })
}

// ============================================
// TASKS
// ============================================
export function useTasks(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['tasks', params],
    queryFn: async () => {
      const { data } = await apiClient.get('/tasks', { params })
      return data
    },
  })
}

export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: ['tasks', id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/tasks/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

export function useTaskStats() {
  return useQuery({
    queryKey: ['tasks', 'stats'],
    queryFn: async () => {
      const { data } = await apiClient.get('/tasks/stats')
      return data.data
    },
  })
}

export function useCreateTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (taskData: any) => {
      const { data } = await apiClient.post('/tasks', taskData)
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...taskData }: any) => {
      const { data } = await apiClient.put(`/tasks/${id}`, taskData)
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useDeleteTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete(`/tasks/${id}`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// ============================================
// APPOINTMENTS
// ============================================
export function useAppointments(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['appointments', params],
    queryFn: async () => {
      const { data } = await apiClient.get('/appointments', { params })
      return data
    },
  })
}

export function useUpcomingAppointments() {
  return useQuery({
    queryKey: ['appointments', 'upcoming'],
    queryFn: async () => {
      const { data } = await apiClient.get('/appointments/upcoming')
      return data.data
    },
  })
}

export function useCreateAppointment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (apptData: any) => {
      const { data } = await apiClient.post('/appointments', apptData)
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateAppointment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...apptData }: any) => {
      const { data } = await apiClient.put(`/appointments/${id}`, apptData)
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// ============================================
// GOALS
// ============================================
export function useGoals(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['goals', params],
    queryFn: async () => {
      const { data } = await apiClient.get('/goals', { params })
      return data
    },
  })
}

export function useCreateGoal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (goalData: any) => {
      const { data } = await apiClient.post('/goals', goalData)
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] })
    },
  })
}

// ============================================
// NOTES
// ============================================
export function useNotes(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['notes', params],
    queryFn: async () => {
      const { data } = await apiClient.get('/notes', { params })
      return data
    },
  })
}

export function useCreateNote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (noteData: any) => {
      const { data } = await apiClient.post('/notes', noteData)
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] })
    },
  })
}

// ============================================
// NOTIFICATIONS
// ============================================
export function useNotifications(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: async () => {
      const { data } = await apiClient.get('/notifications', { params })
      return data
    },
    refetchInterval: 15000, // Poll every 15 seconds for new notifications
  })
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.put(`/notifications/${id}/read`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.put('/notifications/read-all')
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export function useDeleteNotification() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete(`/notifications/${id}`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

// ============================================
// ALL ENTITY HOOKS (generic CRUD via factory)
// ============================================

// Client sub-resources
export const clientFinancialDataHooks = createEntityHooks('client-financial-data', '/client-financial-data')
export const clientPlanningProfileHooks = createEntityHooks('client-planning-profiles', '/client-planning-profiles')
export const clientConsolidatedScoresHooks = createEntityHooks('client-consolidated-scores', '/client-consolidated-scores')
export const clientCustomMetricHooks = createEntityHooks('client-custom-metrics', '/client-custom-metrics')
export const clientDataScoringHooks = createEntityHooks('client-data-scoring', '/client-data-scoring')
export const clientInvitationHooks = createEntityHooks('client-invitations', '/client-invitations')
export const clientOnboardingHooks = createEntityHooks('client-onboarding', '/client-onboarding')
export const clientOperationalStatusHooks = createEntityHooks('client-operational-status', '/client-operational-status')
export const clientSegmentHooks = createEntityHooks('client-segments', '/client-segments')
export const clientSnapshotHooks = createEntityHooks('client-snapshots', '/client-snapshots')
export const clientTaxProfileHooks = createEntityHooks('client-tax-profiles', '/client-tax-profiles')
export const clientTaxStrategyHooks = createEntityHooks('client-tax-strategies', '/client-tax-strategies')
export const clientWhatIfScenarioHooks = createEntityHooks('client-what-if-scenarios', '/client-what-if-scenarios')
export const clientAdvisorMessageHooks = createEntityHooks('client-advisor-messages', '/client-advisor-messages')

// Planning & Financial
export const advancedRetirementAnalysisHooks = createEntityHooks('advanced-retirement-analysis', '/advanced-retirement-analysis')
export const retirementProjectionHooks = createEntityHooks('retirement-projections', '/retirement-projections')
export const debtAccountHooks = createEntityHooks('debt-accounts', '/debt-accounts')
export const debtPayoffScenarioHooks = createEntityHooks('debt-payoff-scenarios', '/debt-payoff-scenarios')
export const savingsScenarioHooks = createEntityHooks('savings-scenarios', '/savings-scenarios')
export const taxAnalysisHooks = createEntityHooks('tax-analysis', '/tax-analysis')
export const taxScenarioHooks = createEntityHooks('tax-scenarios', '/tax-scenarios')
export const netWorthSnapshotHooks = createEntityHooks('net-worth-snapshots', '/net-worth-snapshots')
export const custodianAccountHooks = createEntityHooks('custodian-accounts', '/custodian-accounts')
export const insurancePolicyHooks = createEntityHooks('insurance-policies', '/insurance-policies')
export const loanHooks = createEntityHooks('loans', '/loans')
export const investmentModelHooks = createEntityHooks('investment-models', '/investment-models')

// Cases & Workflows
export const caseHooks = createEntityHooks('cases', '/cases')
export const caseTemplateHooks = createEntityHooks('case-templates', '/case-templates')
export const workflowHooks = createEntityHooks('workflows', '/workflows')
export const workflowDefinitionHooks = createEntityHooks('workflow-definitions', '/workflow-definitions')
export const workflowTemplateHooks = createEntityHooks('workflow-templates', '/workflow-templates')
export const workflowInstanceHooks = createEntityHooks('workflow-instances', '/workflow-instances')
export const workflowExecutionHooks = createEntityHooks('workflow-executions', '/workflow-executions')
export const workflowActionHooks = createEntityHooks('workflow-actions', '/workflow-actions')
export const internalWorkflowTemplateHooks = createEntityHooks('internal-workflow-templates', '/internal-workflow-templates')
export const taskflowHooks = createEntityHooks('taskflows', '/taskflows')

// Tasks & Automation
export const taskTemplateHooks = createEntityHooks('task-templates', '/task-templates')
export const taskTypeHooks = createEntityHooks('task-types', '/task-types')
export const automationHooks = createEntityHooks('automations', '/automations')
export const automationExecutionHooks = createEntityHooks('automation-executions', '/automation-executions')
export const actionHooks = createEntityHooks('actions', '/actions')
export const actionTemplateHooks = createEntityHooks('action-templates', '/action-templates')

// Forms & Questionnaires
export const formHooks = createEntityHooks('forms', '/forms')
export const formVersionHooks = createEntityHooks('form-versions', '/form-versions')
export const formAssignmentHooks = createEntityHooks('form-assignments', '/form-assignments')
export const questionnaireHooks = createEntityHooks('questionnaires', '/questionnaires')
export const questionnaireVersionHooks = createEntityHooks('questionnaire-versions', '/questionnaire-versions')
export const formResponseHooks = createEntityHooks('responses', '/responses')

// Documents
export const documentTemplateHooks = createEntityHooks('document-templates', '/document-templates')
export const documentTemplateFolderHooks = createEntityHooks('document-template-folders', '/document-template-folders')
export const documentSigningRequestHooks = createEntityHooks('document-signing-requests', '/document-signing-requests')
export const documentActivityHooks = createEntityHooks('document-activities', '/document-activities')
export const vaultDocumentHooks = createEntityHooks('vault-documents', '/vault-documents')
export const folderHooks = createEntityHooks('folders', '/folders')
export const globalDocumentFolderHooks = createEntityHooks('global-document-folders', '/global-document-folders')

// Reviews
export const reviewSessionHooks = createEntityHooks('review-sessions', '/review-sessions')
export const reviewTemplateHooks = createEntityHooks('review-templates', '/review-templates')

// Campaigns & Marketing
export const campaignHooks = createEntityHooks('campaigns', '/campaigns')
export const campaignActivityHooks = createEntityHooks('campaign-activities', '/campaign-activities')
export const campaignEnrollmentHooks = createEntityHooks('campaign-enrollments', '/campaign-enrollments')
export const emailTemplateHooks = createEntityHooks('email-templates', '/email-templates')

// Calendar
export const availabilityHooks = createEntityHooks('availability', '/availability')
export const calendarSettingsHooks = createEntityHooks('calendar-settings', '/calendar-settings')
export const appointmentTemplateHooks = createEntityHooks('appointment-templates', '/appointment-templates')

// Compliance
export const complianceRecommendationHooks = createEntityHooks('compliance-recommendations', '/compliance-recommendations')
export const auditEventHooks = createEntityHooks('audit-events', '/audit-events')
export const exceptionCaseHooks = createEntityHooks('exception-cases', '/exception-cases')
export const ruleDefinitionHooks = createEntityHooks('rule-definitions', '/rule-definitions')

// Users & Roles
export const userHooks = createEntityHooks('users', '/users')
export const roleHooks = createEntityHooks('roles', '/roles')
export const teamHooks = createEntityHooks('teams', '/teams')
export const teamNoteHooks = createEntityHooks('team-notes', '/team-notes')
export const trustedContactHooks = createEntityHooks('trusted-contacts', '/trusted-contacts')

// Settings & Configuration
export const globalSettingHooks = createEntityHooks('global-settings', '/global-settings')
export const integrationSettingsHooks = createEntityHooks('integration-settings', '/integration-settings')
export const customClientFieldHooks = createEntityHooks('custom-client-fields', '/custom-client-fields')
export const customClientFieldValueHooks = createEntityHooks('custom-client-field-values', '/custom-client-field-values')
export const customDataFieldTemplateHooks = createEntityHooks('custom-data-field-templates', '/custom-data-field-templates')
export const dataTagHooks = createEntityHooks('data-tags', '/data-tags')
export const scoringTemplateHooks = createEntityHooks('scoring-templates', '/scoring-templates')
export const userPreferenceHooks = createEntityHooks('user-preferences', '/user-preferences')
export const userDashboardHooks = createEntityHooks('user-dashboards', '/user-dashboards')
export const userNotificationSettingHooks = createEntityHooks('user-notification-settings', '/user-notification-settings')
export const customWidgetHooks = createEntityHooks('custom-widgets', '/custom-widgets')
export const quickActionConfigHooks = createEntityHooks('quick-action-configs', '/quick-action-configs')
export const oliPersonalizationHooks = createEntityHooks('oli-personalization', '/oli-personalization')

// Other
export const recommendationHooks = createEntityHooks('recommendations', '/recommendations')
export const advisorPlanHooks = createEntityHooks('advisor-plans', '/advisor-plans')
export const applicationReleaseHooks = createEntityHooks('application-releases', '/application-releases')
export const pipelineHooks = createEntityHooks('pipelines', '/pipelines')
export const leadPipelineStageHooks = createEntityHooks('lead-pipeline-stages', '/lead-pipeline-stages')
export const leadHooks = createEntityHooks('leads', '/leads')
export const locationHooks = createEntityHooks('locations', '/locations')
export const marketplacePackageHooks = createEntityHooks('marketplace-packages', '/marketplace-packages')
export const packageDownloadHooks = createEntityHooks('package-downloads', '/package-downloads')
export const packagePurchaseHooks = createEntityHooks('package-purchases', '/package-purchases')
export const plaidItemHooks = createEntityHooks('plaid-items', '/plaid-items')
export const exportJobHooks = createEntityHooks('export-jobs', '/export-jobs')
export const mergeLogHooks = createEntityHooks('merge-logs', '/merge-logs')
export const goalTemplateHooks = createEntityHooks('goal-templates', '/goal-templates')
export const onboardingTemplateHooks = createEntityHooks('onboarding-templates', '/onboarding-templates')
export const householdHooks = createEntityHooks('households', '/households')
export const communicationHooks = createEntityHooks('communications', '/communications')
export const activityLogHooks = createEntityHooks('activity-logs', '/activity-logs')
export const documentHooks = createEntityHooks('documents', '/documents')

// ============================================
// FUNCTION HOOKS
// ============================================

// Financial
export function useCalculateFinancialHealth() { return useInvokeFunction('calculateFinancialHealth') }
export function useCalculateFinancialMetrics() { return useInvokeFunction('calculateFinancialMetrics') }
export function useCalculateClientPillarScores() { return useInvokeFunction('calculateClientPillarScores') }
export function useCalculateHouseholdScores() { return useInvokeFunction('calculateHouseholdScores') }

// Tax
export function useCalculateTaxLiability() { return useInvokeFunction('calculateTaxLiability') }
export function useProjectTaxScenario() { return useInvokeFunction('projectTaxScenario') }
export function useAnalyzeTaxDocument() { return useInvokeFunction('analyzeTaxDocument') }

// Retirement
export function useComputeRetirementPlan() { return useInvokeFunction('computeRetirementPlan') }
export function useGenerateRetirementStrategies() { return useInvokeFunction('generateRetirementStrategies') }

// Client Analysis
export function useAnalyzeClientData() { return useInvokeFunction('analyzeClientData') }
export function useCaptureProfileSnapshots() { return useInvokeFunction('captureProfileSnapshots') }
export function useEvaluateStatusTransitions() { return useInvokeFunction('evaluateStatusTransitions') }
export function useEvaluateSegmentCriteria() { return useInvokeFunction('evaluateSegmentCriteria') }
export function useEvaluateLeadCriteria() { return useInvokeFunction('evaluateLeadCriteria') }

// Automation
export function useHandleAutomationTrigger() { return useInvokeFunction('handleAutomationTrigger') }
export function useRunAutomationManually() { return useInvokeFunction('runAutomationManually') }
export function useCalculateNextExecutionDate() { return useInvokeFunction('calculateNextExecutionDate') }
export function useGetAutomationMonitorStats() { return useInvokeFunction('getAutomationMonitorStats') }

// Notifications
export function useDispatchNotification() { return useInvokeFunction('dispatchNotification') }
export function useProcessEntityUpdateAndNotify() { return useInvokeFunction('processEntityUpdateAndNotify') }

// Validate
export function useValidateClientOperation() { return useInvokeFunction('validateClientOperation') }

// Email / SMS
export function useSendEmail() { return useInvokeFunction('sendEmail') }
export function useSendSms() { return useInvokeFunction('sendSms') }

// Documents
export function useGetDocumentSignedUrl() { return useInvokeFunction('getDocumentSignedUrl') }
export function useGetDocumentPreview() { return useInvokeFunction('getDocumentPreview') }
export function useGenerateSignedDocument() { return useInvokeFunction('generateSignedDocument') }
export function useFlattenPdfFields() { return useInvokeFunction('flattenPdfFields') }

// Reviews
export function useGenerateReviewTemplate() { return useInvokeFunction('generateReviewTemplate') }
export function useAnalyzeReviewTranscription() { return useInvokeFunction('analyzeReviewTranscription') }
export function useProcessReviewTranscription() { return useInvokeFunction('processReviewTranscription') }

// Search
export function useSearchClients() { return useInvokeFunction('searchClients') }

// Plaid
export function useCreatePlaidLinkToken() { return useInvokeFunction('createPlaidLinkToken') }
export function useExchangePlaidPublicToken() { return useInvokeFunction('exchangePlaidPublicToken') }
export function useSyncPlaidData() { return useInvokeFunction('syncPlaidData') }

// Marketplace
export function useNotifyPackageUpdate() { return useInvokeFunction('notifyPackageUpdate') }
export function useUpdatePackageRating() { return useInvokeFunction('updatePackageRating') }
export function useImportPackage() { return useInvokeFunction('importPackage') }
export function useGetMarketplaceAnalytics() { return useInvokeFunction('getMarketplaceAnalytics') }

// Forms
export function useAssignForm() { return useInvokeFunction('assignForm') }
export function useGetPublicForm() { return useInvokeFunction('getPublicForm') }
export function useSubmitPublicForm() { return useInvokeFunction('submitPublicForm') }

// Workflows
export function useRemapWorkflowData() { return useInvokeFunction('remapWorkflowData') }

// Payments
export function useCreatePaymentIntent() { return useInvokeFunction('createPaymentIntent') }
export function useCancelSubscription() { return useInvokeFunction('cancelSubscription') }

// Change Logs
export function useGetChangeLogs() { return useInvokeFunction('getChangeLogs') }

// Stock / Market
export function useGetStockPerformance() { return useInvokeFunction('getStockPerformance') }

// Admin
export function useUpdateUserSuperAdmin() { return useInvokeFunction('updateUserSuperAdmin') }

// ============================================
// INTEGRATION HOOKS
// ============================================
export function useInvokeLLM() {
  return useMutation({
    mutationFn: async (params: { prompt: string; model?: string; response_json_schema?: any }) => {
      const { data } = await apiClient.post('/integrations/llm', params)
      return data?.data ?? data
    },
  })
}

export function useUploadFile() {
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await apiClient.post('/integrations/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data?.data ?? data
    },
  })
}

export function useUploadPrivateFile() {
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await apiClient.post('/integrations/upload-private', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data?.data ?? data
    },
  })
}

export function useCreateFileSignedUrl() {
  return useMutation({
    mutationFn: async (params: { file_uri: string }) => {
      const { data } = await apiClient.post('/integrations/signed-url', params)
      return data?.data ?? data
    },
  })
}

export function useExtractData() {
  return useMutation({
    mutationFn: async (params: { file_uri: string }) => {
      const { data } = await apiClient.post('/integrations/extract-data', params)
      return data?.data ?? data
    },
  })
}
